import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { API } from "../../../utils/Api";
import { KeyboardArrowLeftOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function OnlineSalesList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));

  const [platform, setPlatform] = useState("ALL");
  const [startDate, setStartDate] = useState(todayYYYYMMDD());
  const [endDate, setEndDate] = useState(todayYYYYMMDD());
  const [search, setSearch] = useState("");

  // Delete modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // View modal state (eye icon)
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const getSales = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API.BASE_URL}third-party-sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          outlet: outletData?._id,
          startDate: startDate,
          endDate: endDate,
        },
      });

      const list = res?.data?.data?.dbResponse ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error(error);
      setErr(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load third party sales."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const platforms = useMemo(() => {
    const set = new Set((rows || []).map((r) => r?.platform).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [rows]);

  // Helpers
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

  const inDateRange = (rowIso) => {
    if (!startDate && !endDate) return true;
    if (!rowIso) return false;

    const rowDay = new Date(rowIso);
    if (Number.isNaN(rowDay.getTime())) return false;

    const rowYMD = rowDay.toISOString().slice(0, 10);
    if (startDate && rowYMD < startDate) return false;
    if (endDate && rowYMD > endDate) return false;
    return true;
  };

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();

    return (rows || []).filter((r) => {
      const matchesPlatform =
        platform === "ALL" ? true : r?.platform === platform;

      const dateIso = r?.createdAt || r?.date;
      const matchesRange = inDateRange(dateIso);

      const productName = r?.product?.name || "";
      const outletName = r?.outlet?.name || "";
      const addonText = (r?.addons || [])
        .map((a) => `${a?.addonTitle || ""} ${a?.addonValueTitle || ""}`.trim())
        .join(" | ");

      const matchesSearch = !s
        ? true
        : `${productName} ${outletName} ${addonText} ${r?.platform || ""}`
          .toLowerCase()
          .includes(s);

      return matchesPlatform && matchesRange && matchesSearch;
    });
  }, [rows, platform, startDate, endDate, search]);

  const summary = useMemo(() => {
    const totalOrders = filteredRows.length;
    const totalQty = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.quantity) || 0),
      0
    );
    const gross = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.price) || 0),
      0
    );
    const discount = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.discount) || 0),
      0
    );
    const net = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.total) || 0),
      0
    );
    return { totalOrders, totalQty, gross, discount, net };
  }, [filteredRows]);

  const platformChipSx = (p = "") => {
    const key = String(p).toLowerCase();

    if (key.includes("zomato")) {
      return {
        backgroundColor: "#E23744",
        fontSize: "12px",
        color: "#fff",
        borderColor: "#E23744",
        "& .MuiChip-label": { color: "#fff" },
      };
    }

    if (key.includes("swiggy")) {
      return {
        backgroundColor: "#FC8019",
        fontSize: "12px",
        color: "#fff",
        borderColor: "#FC8019",
        "& .MuiChip-label": { color: "#fff" },
      };
    }

    return {
      backgroundColor: "#003049",
      color: "#fff",
      borderColor: "#003049",
      "& .MuiChip-label": { color: "#fff" },
    };
  };

  // Delete
  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setSelectedRow(null);
  };

  const deleteEntry = async () => {
    const deleteId = selectedRow?._id;

    if (!deleteId) {
      setErr("Unable to delete: missing entry id.");
      closeDeleteModal();
      return;
    }

    setDeleting(true);
    setErr("");

    try {
      await axios.delete(`${API.BASE_URL}third-party-sales`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { _id: deleteId },
      });

      setRows((prev) => (prev || []).filter((r) => r?._id !== deleteId));
      closeDeleteModal();
    } catch (error) {
      console.error(error);
      setErr(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete the entry."
      );
    } finally {
      setDeleting(false);
    }
  };

  // View (Eye icon)
  const openViewModal = (row) => {
    setViewRow(row);
    setViewOpen(true);
  };

  const closeViewModal = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  const headSx = {
    fontWeight: 900,
    color: "#0f172a",
    background: "#f1f5f9",
    borderBottom: "1px solid #e2e8f0",
    position: "sticky",
    top: 0,
    zIndex: 2,
    whiteSpace: "nowrap",
  };

  const detailLabelSx = { fontSize: 12, color: "#64748b", fontWeight: 800 };
  const detailValueSx = { fontSize: 13, color: "#0f172a", fontWeight: 900 };

  return (
    <Box
      sx={{
        background: "#f6f8fb",
        justifyContent: "center",
        minHeight: "100vh",
        overflowX: "auto",
      }}
    >
      <Toolbar />
      <Grid
        container
        justifyContent="center"
        sx={{ pb: 6, px: { xs: 2, md: 0 } }}
      >
        <Grid item xs={12} md={11} lg={10}>
          {/* Header */}
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background:
                "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack flexDirection={'row'} alignItems={'center'}>
                  <KeyboardArrowLeftOutlined sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} fontSize="large" />
                  <Typography
                    sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}
                  >
                    Third Party Sales
                  </Typography>
                </Stack>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${filteredRows?.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    Search
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    Platform
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                      },
                    }}
                  >
                    <Select
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                      displayEmpty
                    >
                      {platforms.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    From Date
                  </Typography>

                  <TextField
                    fullWidth
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    To Date
                  </Typography>

                  <TextField
                    fullWidth
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={1}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                      textAlign: "center",
                    }}
                  >
                    Refresh
                  </Typography>

                  <Chip
                    onClick={getSales}
                    label={loading ? "..." : "Get"}
                    sx={{
                      width: "100%",
                      height: 52,
                      borderRadius: 2,
                      cursor: "pointer",
                      fontWeight: 900,
                      backgroundColor: "#003049",
                      color: "#fff",
                      "& .MuiChip-label": { color: "#fff" },
                      "&:hover": { backgroundColor: "#00263a" },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Summary + Table */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      icon={<FilterAltOutlined />}
                      label={`Orders: ${summary.totalOrders}`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    />
                    <Chip
                      label={`Qty: ${summary.totalQty}`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    />
                    <Chip
                      label={`Gross: ${money(summary.gross)}`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    />
                    <Chip
                      label={`Discount: ${money(summary.discount)}`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    />
                  </Stack>

                  <Chip
                    label={`Net: ${money(summary.net)}`}
                    sx={{
                      fontWeight: 900,
                      borderRadius: 2,
                      backgroundColor: "#003049",
                      color: "#fff",
                      "& .MuiChip-label": { color: "#fff" },
                    }}
                  />
                </Stack>
              </Box>

              {loading ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                    Loading Sales...
                  </Typography>
                </Box>
              ) : err ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 900, color: "#b91c1c" }}
                  >
                    {err}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Please check token / API and try again.
                  </Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}
                  >
                    No third party sales to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Try changing filters or search.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      maxHeight: "70vh",
                      overflow: "auto",
                    }}
                  >
                    <Table
                      stickyHeader
                      size="small"
                      sx={{
                        tableLayout: "fixed",
                        "& th, & td": {
                          px: { xs: 0.8, sm: 1.2 },
                          py: { xs: 0.7, sm: 0.9 },
                          fontSize: { xs: 11, sm: 12 },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={headSx}>
                            No.
                          </TableCell>
                          <TableCell align="left" sx={headSx}>
                            Date
                          </TableCell>
                          <TableCell align="left" sx={headSx}>
                            Platform
                          </TableCell>
                          <TableCell align="left" sx={headSx}>
                            Product
                          </TableCell>
                          <TableCell align="center" sx={headSx}>
                            Qty
                          </TableCell>

                          {!isSm && (
                            <TableCell align="center" sx={headSx}>
                              Price
                            </TableCell>
                          )}
                          {!isSm && (
                            <TableCell align="center" sx={headSx}>
                              Discount
                            </TableCell>
                          )}

                          <TableCell align="center" sx={headSx}>
                            Total
                          </TableCell>

                          {/* New: View icon column (doesn't affect width much) */}
                          <TableCell align="center" sx={headSx}>
                            View
                          </TableCell>

                          {!isSm && (
                            <TableCell align="center" sx={headSx}>
                              Delete
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredRows.map((r, index) => {
                          const productName = r?.product?.name || "N/A";
                          const addons = Array.isArray(r?.addons) ? r.addons : [];

                          return (
                            <TableRow
                              key={r?._id || index}
                              hover
                              sx={{
                                backgroundColor:
                                  index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell
                                align="center"
                                sx={{ fontWeight: 700, color: "#334155" }}
                              >
                                {index + 1}
                              </TableCell>

                              <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: "#0f172a",
                                  }}
                                >
                                  {formatDateTime(r?.createdAt || r?.date)}
                                </Typography>
                              </TableCell>

                              <TableCell align="left">
                                <Chip
                                  size="small"
                                  label={r?.platform || "N/A"}
                                  sx={{
                                    fontWeight: 900,
                                    borderRadius: 2,
                                    ...platformChipSx(r?.platform),
                                  }}
                                />
                              </TableCell>

                              <TableCell align="left" sx={{ whiteSpace: "normal" }}>
                                <Typography
                                  sx={{
                                    fontSize: { xs: 12, sm: 14 },
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    lineHeight: 1.2,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {productName}
                                </Typography>

                                {addons.length === 0 ? (
                                  <Typography
                                    sx={{ fontSize: 11, color: "#64748b", mt: 0.3 }}
                                  >
                                    No addons
                                  </Typography>
                                ) : (
                                  <Stack
                                    direction="row"
                                    flexWrap="wrap"
                                    gap={0.6}
                                    sx={{ mt: 0.6 }}
                                  >
                                    {addons.map((a, i) => (
                                      <Chip
                                        key={a?._id || i}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 2,
                                          fontWeight: 700,
                                          height: 22,
                                          "& .MuiChip-label": {
                                            px: 0.75,
                                            fontSize: 11,
                                          },
                                        }}
                                        label={`${a?.addonTitle || ""}: ${a?.addonValueTitle || ""
                                          }`}
                                      />
                                    ))}
                                  </Stack>
                                )}

                                {/* Mobile-only: keep width same (no extra columns) */}
                                {isSm && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{ mt: 0.8 }}
                                  >
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`Price: ${money(r?.price)}`}
                                      sx={{ borderRadius: 2, fontWeight: 800 }}
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`Disc: ${money(r?.discount)}`}
                                      sx={{ borderRadius: 2, fontWeight: 800 }}
                                    />
                                    <Button
                                      onClick={() => openDeleteModal(r)}
                                      size="small"
                                      variant="outlined"
                                      sx={{ borderRadius: 2, fontWeight: 900, py: 0.2 }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                )}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900 }}>
                                {Number(r?.quantity || 0)}
                              </TableCell>

                              {!isSm && (
                                <TableCell align="center" sx={{ fontWeight: 700 }}>
                                  {money(r?.price)}
                                </TableCell>
                              )}

                              {!isSm && (
                                <TableCell align="center" sx={{ fontWeight: 700 }}>
                                  {money(r?.discount)}
                                </TableCell>
                              )}

                              <TableCell
                                align="center"
                                sx={{ fontWeight: 900, color: "#0f172a" }}
                              >
                                {money(r?.total)}
                              </TableCell>

                              {/* View icon cell */}
                              <TableCell align="center">
                                <IconButton
                                  onClick={() => openViewModal(r)}
                                  size="small"
                                  sx={{
                                    borderRadius: 2,
                                    border: "1px solid #e2e8f0",
                                    background: "#ffffff",
                                    "&:hover": { background: "#f8fafc" },
                                  }}
                                  title="View details"
                                >
                                  <VisibilityOutlined sx={{ color: "#0f172a" }} />
                                </IconButton>
                              </TableCell>

                              {/* Desktop delete icon cell */}
                              {!isSm && (
                                <TableCell align="center">
                                  <IconButton
                                    onClick={() => openDeleteModal(r)}
                                    size="small"
                                    sx={{
                                      borderRadius: 2,
                                      border: "1px solid #fee2e2",
                                      background: "#fff5f5",
                                      "&:hover": { background: "#ffecec" },
                                    }}
                                    title="Delete entry"
                                  >
                                    <DeleteOutlineRounded sx={{ color: "#b91c1c" }} />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* View Details Modal */}
      <Dialog open={viewOpen} onClose={closeViewModal} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "#0f172a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Sale Details
          <IconButton onClick={closeViewModal}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ background: "#fff" }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #e6eaf0",
              background: "#f8fafc",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography sx={detailLabelSx}>Platform</Typography>
                <Typography sx={detailValueSx}>{viewRow?.platform || "N/A"}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography sx={detailLabelSx}>Date</Typography>
                <Typography sx={detailValueSx}>
                  {formatDateTime(viewRow?.createdAt || viewRow?.date)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography sx={detailLabelSx}>Outlet</Typography>
                <Typography sx={detailValueSx}>{viewRow?.outlet?.name || "N/A"}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography sx={detailLabelSx}>Product</Typography>
                <Typography sx={detailValueSx}>{viewRow?.product?.name || "N/A"}</Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography sx={detailLabelSx}>Qty</Typography>
                <Typography sx={detailValueSx}>{Number(viewRow?.quantity || 0)}</Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography sx={detailLabelSx}>Price</Typography>
                <Typography sx={detailValueSx}>{money(viewRow?.price)}</Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography sx={detailLabelSx}>Discount</Typography>
                <Typography sx={detailValueSx}>{money(viewRow?.discount)}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography sx={detailLabelSx}>Total</Typography>
                <Typography sx={{ ...detailValueSx, fontSize: 16 }}>
                  {money(viewRow?.total)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ ...detailLabelSx, marginTop: 0.5 }}>Addons</Typography>
                {Array.isArray(viewRow?.addons) && viewRow.addons.length > 0 ? (
                  <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mt: 0.8 }}>
                    {viewRow.addons.map((a, i) => (
                      <Chip
                        key={a?._id || i}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 800,
                          "& .MuiChip-label": { fontSize: 12 },
                        }}
                        label={`${a?.addonTitle || ""}: ${a?.addonValueTitle || ""}`}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 700, mt: 0.5 }}>
                    No addons
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeViewModal} variant="outlined" sx={{ borderRadius: 2, fontWeight: 900 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteOpen} onClose={closeDeleteModal} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "#0f172a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Confirm Delete
          <IconButton onClick={closeDeleteModal} disabled={deleting}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ background: "#fff" }}>
          <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 700 }}>
            Are you sure you want to delete this entry? This action cannot be undone.
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #e6eaf0",
              background: "#f8fafc",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              Platform
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 900 }}>
              {selectedRow?.platform || "N/A"}
            </Typography>

            <Typography sx={{ mt: 1, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              Product
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 900 }}>
              {selectedRow?.product?.name || "N/A"}
            </Typography>

            <Typography sx={{ mt: 1, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              Date
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 900 }}>
              {formatDateTime(selectedRow?.createdAt || selectedRow?.date)}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeDeleteModal}
            disabled={deleting}
            variant="outlined"
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Cancel
          </Button>

          <Button
            onClick={deleteEntry}
            disabled={deleting}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              backgroundColor: "#b91c1c",
              "&:hover": { backgroundColor: "#991b1b" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OnlineSalesList;